import { useState, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Trash2,
  Truck,
  Hammer,
  Wrench,
  Camera,
  ArrowRight,
  ArrowLeft,
  Check,
  Loader2,
  Phone,
} from "lucide-react";
import { z } from "zod";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { trackGenerateLead } from "@/lib/analytics";

type ServiceType = "junk_removal" | "construction_debris" | "handyman";
type Timing = "asap" | "this_week" | "flexible";

const SERVICES: { id: ServiceType; label: string; icon: typeof Trash2; desc: string }[] = [
  { id: "junk_removal", label: "Junk Removal", icon: Trash2, desc: "Furniture, debris, yard waste" },
  { id: "construction_debris", label: "Construction Debris", icon: Hammer, desc: "Drywall, concrete, demo cleanup" },
  { id: "handyman", label: "Handyman Work", icon: Wrench, desc: "Repairs, installs, small projects" },
];

const TIMING: { id: Timing; label: string; sub: string }[] = [
  { id: "asap", label: "ASAP", sub: "Today" },
  { id: "this_week", label: "This Week", sub: "Within 7 days" },
  { id: "flexible", label: "Flexible", sub: "No rush" },
];

const contactSchema = z.object({
  name: z.string().trim().min(2, "Please enter your name").max(100),
  phone: z
    .string()
    .trim()
    .min(7, "Please enter a valid phone number")
    .max(30),
  instagram: z.string().trim().max(60).optional().or(z.literal("")),
});

const MAX_PHOTOS = 5;
const MIN_PHOTOS = 3;
const MAX_BYTES = 10 * 1024 * 1024; // 10MB

interface LeadFunnelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type NotifyLeadResponse = {
  ok?: boolean;
  lead_id?: string;
  photo_urls?: string[];
  error?: string;
};

export const LeadFunnel = ({ open, onOpenChange }: LeadFunnelProps) => {
  const [step, setStep] = useState(1);
  const [service, setService] = useState<ServiceType | null>(null);
  const [photos, setPhotos] = useState<File[]>([]);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [instagram, setInstagram] = useState("");
  const [timing, setTiming] = useState<Timing | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const reset = useCallback(() => {
    setStep(1);
    setService(null);
    setPhotos([]);
    setName("");
    setPhone("");
    setInstagram("");
    setTiming(null);
    setSubmitting(false);
    setSuccess(false);
  }, []);

  const handleClose = (next: boolean) => {
    if (!next) {
      // small delay so closing animation doesn't show reset state
      setTimeout(reset, 200);
    }
    onOpenChange(next);
  };

  const onPhotos = (files: FileList | null) => {
    if (!files) return;
    const incoming = Array.from(files);
    const oversize = incoming.find((f) => f.size > MAX_BYTES);
    if (oversize) {
      toast.error(`"${oversize.name}" is too large (max 10MB)`);
      return;
    }
    const next = [...photos, ...incoming].slice(0, MAX_PHOTOS);
    setPhotos(next);
  };

  const removePhoto = (idx: number) => {
    setPhotos(photos.filter((_, i) => i !== idx));
  };

  const submit = async () => {
    const parsed = contactSchema.safeParse({ name, phone, instagram });
    if (!parsed.success) {
      toast.error(parsed.error.issues[0].message);
      return;
    }
    if (!service || !timing || photos.length < MIN_PHOTOS) {
      toast.error("Please complete all steps");
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("service_type", service);
      formData.append("name", parsed.data.name);
      formData.append("phone", parsed.data.phone);
      formData.append("instagram", parsed.data.instagram || "");
      formData.append("timing", timing);
      photos.forEach((photo) => formData.append("photos", photo, photo.name));

      const { data, error } = await supabase.functions.invoke<NotifyLeadResponse>("notify-lead", {
        body: formData,
      });

      if (error || !data?.ok || !data.lead_id || data.photo_urls?.length !== photos.length) {
        console.error("Lead submission failed", { error, data });
        throw new Error(data?.error || error?.message || "Submission failed");
      }

      trackGenerateLead({
        service_type: service,
        timing,
        photo_count: photos.length,
      });

      setSuccess(true);
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again or call us.");
    } finally {
      setSubmitting(false);
    }
  };

  const canNext =
    (step === 1 && service) ||
    (step === 2 && photos.length >= MIN_PHOTOS) ||
    (step === 3 && name.trim().length >= 2 && phone.trim().length >= 7);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-charcoal text-warm-white border-warm-white/15 max-w-xl p-0 gap-0 max-h-[92vh] overflow-y-auto">
        <DialogHeader className="p-6 pb-3 border-b border-warm-white/10">
          <DialogTitle className="font-display text-xl text-warm-white tracking-wide">
            {success ? "Got It — We're On It" : "Get My Estimate"}
          </DialogTitle>
          <DialogDescription className="text-warm-white/60 text-xs uppercase tracking-[0.2em]">
            {success ? "Submitted" : `Step ${step} of 4`}
          </DialogDescription>
          {!success && (
            <div className="mt-3 flex gap-1.5">
              {[1, 2, 3, 4].map((s) => (
                <div
                  key={s}
                  className={`h-1 flex-1 rounded-full transition-colors ${
                    s <= step ? "bg-brass" : "bg-warm-white/15"
                  }`}
                />
              ))}
            </div>
          )}
        </DialogHeader>

        <div className="p-6 space-y-6">
          {success ? (
            <div className="space-y-5 text-center py-4">
              <div className="mx-auto h-14 w-14 rounded-full bg-brass/15 border border-brass/40 flex items-center justify-center">
                <Check className="h-7 w-7 text-brass" />
              </div>
              <div className="space-y-2">
                <h3 className="font-display text-2xl">We'll review your photos and contact you within minutes.</h3>
                <p className="text-warm-white/65 text-sm">
                  Reply to our text or expect a call from <span className="text-warm-white font-semibold">(213) 986-6277</span>.
                </p>
              </div>
              <div className="pt-2 flex flex-col sm:flex-row gap-3 justify-center">
                <Button asChild variant="brass" size="lg">
                  <a href="tel:+12139866277">
                    <Phone className="h-4 w-4" /> Call Us Now
                  </a>
                </Button>
                <Button variant="outlineLight" size="lg" onClick={() => handleClose(false)}>
                  Done
                </Button>
              </div>
            </div>
          ) : step === 1 ? (
            <div className="space-y-4">
              <p className="text-warm-white/70 text-sm">What do you need removed or fixed?</p>
              <div className="grid gap-3">
                {SERVICES.map(({ id, label, icon: Icon, desc }) => {
                  const active = service === id;
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setService(id)}
                      className={`flex items-center gap-4 text-left p-4 border-2 transition-all ${
                        active
                          ? "border-brass bg-brass/10"
                          : "border-warm-white/15 hover:border-brass/50 hover:bg-charcoal-soft"
                      }`}
                    >
                      <Icon className={`h-6 w-6 shrink-0 ${active ? "text-brass" : "text-warm-white/70"}`} />
                      <div className="flex-1">
                        <div className="font-display text-base tracking-wide text-warm-white">
                          {label}
                        </div>
                        <div className="text-xs text-warm-white/55 mt-0.5">{desc}</div>
                      </div>
                      {active && <Check className="h-5 w-5 text-brass" />}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : step === 2 ? (
            <div className="space-y-4">
              <div>
                <p className="text-warm-white/70 text-sm">
                  Upload {MIN_PHOTOS}–{MAX_PHOTOS} photos of the job.
                </p>
                <p className="text-warm-white/45 text-xs mt-1">
                  More angles = a faster, more accurate quote.
                </p>
              </div>

              <div className="grid grid-cols-3 gap-3">
                {photos.map((file, i) => (
                  <div
                    key={i}
                    className="relative aspect-square border border-warm-white/15 bg-charcoal-soft overflow-hidden"
                  >
                    <img
                      src={URL.createObjectURL(file)}
                      alt={`upload ${i + 1}`}
                      className="h-full w-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => removePhoto(i)}
                      className="absolute top-1 right-1 bg-charcoal/80 text-warm-white p-1 hover:bg-charcoal"
                      aria-label="Remove photo"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
                {photos.length < MAX_PHOTOS && (
                  <label
                    htmlFor="photo-upload"
                    className="aspect-square border-2 border-dashed border-warm-white/20 hover:border-brass hover:bg-brass/5 flex flex-col items-center justify-center gap-1 cursor-pointer transition-colors"
                  >
                    <Camera className="h-6 w-6 text-warm-white/50" />
                    <span className="text-[10px] uppercase tracking-wider text-warm-white/55">
                      Add Photo
                    </span>
                    <input
                      id="photo-upload"
                      type="file"
                      accept="image/*"
                      multiple
                      capture="environment"
                      className="sr-only"
                      onChange={(e) => onPhotos(e.target.files)}
                    />
                  </label>
                )}
              </div>

              <div className="text-xs text-warm-white/55">
                {photos.length} of {MAX_PHOTOS} added
                {photos.length > 0 && photos.length < MIN_PHOTOS && (
                  <span className="text-brass ml-1">
                    · {MIN_PHOTOS - photos.length} more required
                  </span>
                )}
              </div>
            </div>
          ) : step === 3 ? (
            <div className="space-y-4">
              <p className="text-warm-white/70 text-sm">How can we reach you?</p>
              <div className="space-y-2">
                <Label htmlFor="lead-name" className="text-warm-white/85">Name</Label>
                <Input
                  id="lead-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your full name"
                  maxLength={100}
                  className="bg-charcoal-soft border-warm-white/15 text-warm-white placeholder:text-warm-white/55"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lead-phone" className="text-warm-white/85">Phone</Label>
                <Input
                  id="lead-phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="(323) 555-0100"
                  maxLength={30}
                  className="bg-charcoal-soft border-warm-white/15 text-warm-white placeholder:text-warm-white/55"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lead-ig" className="text-warm-white/85">
                  Instagram <span className="text-warm-white/60 normal-case font-normal">(optional)</span>
                </Label>
                <Input
                  id="lead-ig"
                  value={instagram}
                  onChange={(e) => setInstagram(e.target.value)}
                  placeholder="@yourhandle"
                  maxLength={60}
                  className="bg-charcoal-soft border-warm-white/15 text-warm-white placeholder:text-warm-white/55"
                />
              </div>
            </div>
          ) : (
            // step 4
            <div className="space-y-4">
              <p className="text-warm-white/70 text-sm">When do you need this done?</p>
              <div className="grid gap-3">
                {TIMING.map(({ id, label, sub }) => {
                  const active = timing === id;
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setTiming(id)}
                      className={`flex items-center justify-between text-left p-4 border-2 transition-all ${
                        active
                          ? "border-brass bg-brass/10"
                          : "border-warm-white/15 hover:border-brass/50 hover:bg-charcoal-soft"
                      }`}
                    >
                      <div>
                        <div className="font-display text-base tracking-wide text-warm-white">
                          {label}
                        </div>
                        <div className="text-xs text-warm-white/55 mt-0.5">{sub}</div>
                      </div>
                      {active && <Check className="h-5 w-5 text-brass" />}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {!success && (
          <div className="border-t border-warm-white/10 p-4 flex items-center justify-between gap-3 bg-charcoal-soft/50">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setStep((s) => Math.max(1, s - 1))}
              disabled={step === 1 || submitting}
              className="text-warm-white/70 hover:text-warm-white"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </Button>
            {step < 4 ? (
              <Button
                type="button"
                variant="brass"
                size="lg"
                disabled={!canNext}
                onClick={() => setStep((s) => Math.min(4, s + 1))}
              >
                Continue <ArrowRight className="h-4 w-4" />
              </Button>
            ) : (
              <Button
                type="button"
                variant="brass"
                size="lg"
                disabled={!timing || submitting}
                onClick={submit}
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Submitting…
                  </>
                ) : (
                  <>
                    Submit Request <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
